<template>
  <form @submit.prevent="handleSubmit" class="space-y-8">
    <!-- Section Fiches du Lookbook (seulement en édition) -->
    <LookbookFichesList
      v-if="isEditing"
      ref="fichesSection"
      :fiches="fiches"
      :is-uploading="isUploadingFiche"
      @add-fiche="handleAddFiche"
      @edit-fiche="handleEditFiche"
      @delete-fiche="handleDeleteFiche"
      @set-couverture="handleSetCouverture"
      @change-ordre="handleChangeOrdre"
    />

    <!-- Informations générales - Bannière -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="text-lg font-medium text-gray-900 mb-4">Informations générales</h3>
      
      <!-- Upload de bannière -->
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Bannière de la page lookbook
          </label>
          <div class="flex items-center gap-4">
            <input 
              ref="fileInput"
              type="file" 
              accept="image/*"
              @change="handleFileChange"
              class="hidden"
            />
            <button 
              type="button"
              @click="fileInput?.click()"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Choisir un fichier
            </button>
            <span class="text-sm text-gray-500">
              {{ selectedFile ? selectedFile.name : 'Aucun fichier choisi' }}
            </span>
          </div>
        </div>
        
        <!-- Aperçu de la bannière -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Aperçu de la bannière de la page lookbook
          </label>
          <div class="w-full h-48 bg-gray-200 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
            <div v-if="!previewUrl" class="text-center">
              <div class="text-gray-500 text-sm">max-width:1280px</div>
            </div>
            <img v-else :src="previewUrl" alt="Aperçu bannière" class="max-w-full max-h-full object-contain rounded-lg" />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Champs de formulaire -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Colonne gauche -->
      <div class="space-y-6">
        <!-- Année (Collection) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Collection (Année)</label>
          <select 
            v-model="formData.annee"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">-- Sélectionner une année --</option>
            <option v-for="year in availableYears" :key="year" :value="year.toString()">
              {{ year }}
            </option>
          </select>
        </div>
        
        <!-- Saison -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Saison</label>
          <select 
            v-model="formData.collection"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">-- Sélectionner une saison --</option>
            <option value="printemps-ete">Printemps / Été</option>
            <option value="automne-hiver">Automne / Hiver</option>
          </select>
        </div>
        
        <!-- Couleurs -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Couleur du cadre</label>
            <div class="relative">
              <input 
                ref="couleurCadreColorPicker"
                v-model="couleurCadreHex"
                type="color"
                class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              />
              <div 
                class="flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:border-indigo-400 transition-colors"
                @click="couleurCadreColorPicker?.click()"
              >
                <div 
                  class="w-8 h-8 rounded border border-gray-300"
                  :style="{ backgroundColor: couleurCadreHex }"
                ></div>
            <input 
                  v-model="couleurCadreText"
              type="text"
              placeholder="FFFFFF"
                  maxlength="6"
                  @click.stop
                  class="flex-1 outline-none uppercase"
            />
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Couleur du texte</label>
            <div class="relative">
              <input 
                ref="couleurTexteColorPicker"
                v-model="couleurTexteHex"
                type="color"
                class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              />
              <div 
                class="flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:border-indigo-400 transition-colors"
                @click="couleurTexteColorPicker?.click()"
              >
                <div 
                  class="w-8 h-8 rounded border border-gray-300"
                  :style="{ backgroundColor: couleurTexteHex }"
                ></div>
            <input 
                  v-model="couleurTexteText"
              type="text"
              placeholder="000000"
                  maxlength="6"
                  @click.stop
                  class="flex-1 outline-none uppercase"
            />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Colonne droite - Titres multilingues -->
      <div class="space-y-6">
        <!-- Titre multilingue -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Titre</label>
          <div class="space-y-3">
            <div class="flex items-center gap-3">
                              <img src="@/assets/images/france.png" alt="Français" class="w-6 h-4" />
              <input 
                v-model="formData.titre.francais"
                type="text"
                placeholder="-"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div class="flex items-center gap-3">
                              <img src="@/assets/images/royaume-uni.png" alt="Anglais" class="w-6 h-4" />
              <input 
                v-model="formData.titre.anglais"
                type="text"
                placeholder="-"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div class="flex items-center gap-3">
                              <img src="@/assets/images/espagne.png" alt="Espagnol" class="w-6 h-4" />
              <input 
                v-model="formData.titre.espagnol"
                type="text"
                placeholder="-"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div class="flex items-center gap-3">
                              <img src="@/assets/images/italie.png" alt="Italien" class="w-6 h-4" />
              <input 
                v-model="formData.titre.italien"
                type="text"
                placeholder="-"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
        
        <!-- Catégories -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Catégories</label>
          <div class="border border-gray-300 rounded-md p-3 max-h-32 overflow-y-auto">
            <div class="space-y-2">
              <label v-for="category in categories" :key="category.id_categorie" class="flex items-center">
                <input 
                  type="checkbox"
                  :value="category.id_categorie"
                  v-model="formData.categories"
                  class="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span class="text-sm text-gray-700">{{ category.libelle }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Description multilingue -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="text-lg font-medium text-gray-900 mb-4">Description</h3>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div class="space-y-3">
          <div class="flex items-start gap-3">
                            <img src="@/assets/images/france.png" alt="Français" class="w-6 h-4 mt-2" />
            <textarea 
              v-model="formData.description.francais"
              rows="4"
              placeholder="-"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            ></textarea>
          </div>
          <div class="flex items-start gap-3">
                            <img src="@/assets/images/royaume-uni.png" alt="Anglais" class="w-6 h-4 mt-2" />
            <textarea 
              v-model="formData.description.anglais"
              rows="4"
              placeholder="-"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            ></textarea>
          </div>
        </div>
        <div class="space-y-3">
          <div class="flex items-start gap-3">
                            <img src="@/assets/images/espagne.png" alt="Espagnol" class="w-6 h-4 mt-2" />
            <textarea 
              v-model="formData.description.espagnol"
              rows="4"
              placeholder="-"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            ></textarea>
          </div>
          <div class="flex items-start gap-3">
                            <img src="@/assets/images/italie.png" alt="Italien" class="w-6 h-4 mt-2" />
            <textarea 
              v-model="formData.description.italien"
              rows="4"
              placeholder="-"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            ></textarea>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Boutons d'action -->
    <div class="flex justify-end pt-6 border-t border-gray-200">
      <button 
        type="submit"
        class="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <i class="fas fa-check mr-2"></i>
        {{ props.initialData ? 'Modifier' : 'Créer' }}
      </button>
    </div>
  </form>

  <!-- Modal de suppression de fiche -->
  <ModalComp
    :isVisible="showDeleteModal"
    @update:isVisible="showDeleteModal = $event"
    @confirm="confirmDeleteFiche"
    title="Supprimer la fiche"
    content="Êtes-vous sûr de vouloir supprimer cette fiche ? Cette action est irréversible."
    cancelText="Annuler"
    confirmText="Supprimer"
    size="sm"
  >
    <template #header>
      <div class="flex items-center">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mr-3">
          <i class="fas fa-exclamation-triangle text-red-600 text-xl"></i>
        </div>
        <h3 class="text-lg font-medium text-gray-900">Supprimer la fiche</h3>
      </div>
    </template>
    
    <template #footer>
      <button
        @click="closeDeleteModal"
        class="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
      >
        Annuler
      </button>
      <button
        @click="confirmDeleteFiche"
        class="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
      >
        Supprimer
      </button>
    </template>
  </ModalComp>

  <!-- Modal d'édition de fiche -->
  <LookbookFicheEditModal
    :is-visible="showEditModal"
    :fiche="ficheToEdit"
    :is-saving="isSavingFiche"
    @update:is-visible="showEditModal = $event"
    @save="handleSaveFiche"
  />
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue'
import type { LookbookFormData, LookbookFiche } from '@/types/lookbook'
import type { LookbookFicheDisplay } from '@/types/lookbook-fiche'
import ModalComp from '@/components/ui/ModalComp.vue'
import LookbookFichesList from './LookbookFichesList.vue'
import LookbookFicheEditModal from './LookbookFicheEditModal.vue'
import { getMainCategories } from '@/api/categories'
import { 
  getLookbookFiches, 
  createLookbookFiche, 
  deleteLookbookFiche, 
  setFicheCouverture, 
  changeFicheOrdre,
  uploadLookbookFiche,
  updateLookbookFiche,
  getFicheProduits,
  addProduitToFiche,
  removeProduitFromFiche 
} from '@/api/lookbooks'
import type { Produit } from '@/types/produit'
import type { CategorieWithLabel } from '@/types/categorie'
import { useNotification } from '@/composables/useNotification'
import { getSellerImageUrl } from '@/config/api'

interface Props {
  initialData?: Omit<LookbookFormData, 'banniere'> & { banniere?: string; id?: number }
}

interface Emits {
  (e: 'submit', data: LookbookFormData): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File>()
const previewUrl = ref<string>()

// Refs pour les fiches
const fiches = ref<LookbookFicheDisplay[]>([])
const isUploadingFiche = ref(false)
const fichesSection = ref<HTMLElement>()

// Notification composable
const { show } = useNotification()

// Vérifier si on est en mode édition
const isEditing = computed(() => !!props.initialData)

// Refs pour les color pickers
const couleurCadreColorPicker = ref<HTMLInputElement>()
const couleurTexteColorPicker = ref<HTMLInputElement>()

// Générer les années dynamiquement (année courante - 2 jusqu'à année courante + 1)
const availableYears = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i)
  }
  return years
})

// Gestion des couleurs
const couleurCadreText = ref(props.initialData?.couleurCadre || 'FFFFFF')
const couleurTexteText = ref(props.initialData?.couleurTexte || '000000')

const couleurCadreHex = computed({
  get: () => '#' + couleurCadreText.value,
  set: (val: string) => {
    couleurCadreText.value = val.replace('#', '').toUpperCase()
  }
})

const couleurTexteHex = computed({
  get: () => '#' + couleurTexteText.value,
  set: (val: string) => {
    couleurTexteText.value = val.replace('#', '').toUpperCase()
  }
})

// Synchroniser avec formData
watch(couleurCadreText, (newVal) => {
  formData.couleurCadre = newVal
})

watch(couleurTexteText, (newVal) => {
  formData.couleurTexte = newVal
})

// Catégories chargées dynamiquement depuis l'API
const categories = ref<CategorieWithLabel[]>([])

// ============= MODAL ÉDITION FICHE =============
const showEditModal = ref(false)
const ficheToEdit = ref<LookbookFicheDisplay | null>(null)
const isSavingFiche = ref(false)

const handleEditFiche = async (fiche: LookbookFicheDisplay) => {
  ficheToEdit.value = fiche
  showEditModal.value = true
}

const handleSaveFiche = async (data: { 
  fiche: LookbookFicheDisplay
  file?: File
  products: Produit[]
  descriptions: {
    francais: string
    anglais: string
    espagnol: string
    italien: string
  }
}) => {
  if (!data.fiche || !props.initialData?.id) return
  
  isSavingFiche.value = true
  
  try {
    // Si une nouvelle image est sélectionnée, l'uploader
    if (data.file) {
      const uploadResult = await uploadLookbookFiche(
        props.initialData.id,
        data.fiche.id,
        data.file
      )
      if (!uploadResult.success) {
        show('error', uploadResult.message)
        return
      }
    }
    
    // Sauvegarder les descriptions
    await updateLookbookFiche(data.fiche.id, {
      description_fr: data.descriptions.francais,
      description_en: data.descriptions.anglais,
      description_es: data.descriptions.espagnol,
      description_it: data.descriptions.italien,
    })
    
    // Charger les produits initiaux pour la comparaison
    const ficheProduits = await getFicheProduits(data.fiche.id)
    const initialProductIds = ficheProduits.map(fp => fp.id_produit)
    
    // Sauvegarder les produits associés
    const currentProductIds = data.products.map(p => p.id_produit)
    
    // Produits à ajouter (dans currentProductIds mais pas dans initialProductIds)
    const toAdd = currentProductIds.filter(id => !initialProductIds.includes(id))
    
    // Produits à supprimer (dans initialProductIds mais pas dans currentProductIds)
    const toRemove = initialProductIds.filter(id => !currentProductIds.includes(id))
    
    // Ajouter les nouveaux produits
    for (const idProduit of toAdd) {
      await addProduitToFiche(data.fiche.id, idProduit)
    }
    
    // Supprimer les produits retirés
    for (const idProduit of toRemove) {
      await removeProduitFromFiche(data.fiche.id, idProduit)
    }
    
    show('success', 'Fiche mise à jour avec succès')
    await loadFiches()
    showEditModal.value = false
  } catch (error) {
    show('error', 'Erreur lors de la mise à jour de la fiche')
  } finally {
    isSavingFiche.value = false
  }
}

// ============= MODAL SUPPRESSION FICHE =============
const showDeleteModal = ref(false)
const ficheToDelete = ref<LookbookFicheDisplay | null>(null)

const openDeleteModal = (fiche: LookbookFicheDisplay) => {
  ficheToDelete.value = fiche
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  ficheToDelete.value = null
}

const confirmDeleteFiche = async () => {
  if (!ficheToDelete.value) return
  
  try {
    await deleteLookbookFiche(ficheToDelete.value.id)
    await loadFiches()
    show('success', 'Fiche supprimée avec succès')
    closeDeleteModal()
  } catch (error) {
    show('error', 'Erreur lors de la suppression de la fiche')
  }
}

// Charger les catégories au montage du composant
onMounted(async () => {
  try {
    categories.value = await getMainCategories()
  } catch (error) {
    // Erreur silencieuse - les catégories restent vides
  }
})

const formData = reactive<LookbookFormData>({
  titre: {
    francais: props.initialData?.titre.francais || '',
    anglais: props.initialData?.titre.anglais || '',
    espagnol: props.initialData?.titre.espagnol || '',
    italien: props.initialData?.titre.italien || ''
  },
  description: {
    francais: props.initialData?.description.francais || '',
    anglais: props.initialData?.description.anglais || '',
    espagnol: props.initialData?.description.espagnol || '',
    italien: props.initialData?.description.italien || ''
  },
  annee: props.initialData?.annee || new Date().getFullYear().toString(),
  collection: props.initialData?.collection || '', // printemps-ete ou automne-hiver
  couleurCadre: props.initialData?.couleurCadre || 'FFFFFF',
  couleurTexte: props.initialData?.couleurTexte || '000000',
  categories: props.initialData?.categories || []
})

// Watch pour mettre à jour le formulaire quand initialData change (mode édition)
watch(() => props.initialData, (newData) => {
  if (newData) {
    formData.titre = { ...newData.titre }
    formData.description = { ...newData.description }
    formData.annee = newData.annee
    formData.collection = newData.collection
    formData.couleurCadre = newData.couleurCadre
    formData.couleurTexte = newData.couleurTexte
    formData.categories = [...(newData.categories || [])]
    
    // Mettre à jour les color pickers
    couleurCadreText.value = newData.couleurCadre
    couleurTexteText.value = newData.couleurTexte
    
    // Si une bannière existe, l'afficher en prévisualisation avec timestamp pour éviter le cache
    if (newData.banniere) {
      const timestamp = new Date().getTime()
      previewUrl.value = `${newData.banniere}?t=${timestamp}`
    } else {
      previewUrl.value = ''
    }
    selectedFile.value = undefined
  } else {
    // Réinitialiser le formulaire si pas de données (nouveau lookbook)
    resetForm()
  }
}, { deep: true, immediate: true })

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    selectedFile.value = target.files[0]
    formData.banniere = target.files[0]
    
    // Créer l'URL de prévisualisation
    previewUrl.value = URL.createObjectURL(target.files[0])
  }
}

const handleSubmit = () => {
  emit('submit', { ...formData })
}

const resetForm = () => {
  if (props.initialData) {
    formData.titre = { ...props.initialData.titre }
    formData.description = { ...props.initialData.description }
    formData.annee = props.initialData.annee
    formData.collection = props.initialData.collection
    formData.couleurCadre = props.initialData.couleurCadre
    formData.couleurTexte = props.initialData.couleurTexte
    formData.categories = [...props.initialData.categories]
    couleurCadreText.value = props.initialData.couleurCadre
    couleurTexteText.value = props.initialData.couleurTexte
    previewUrl.value = props.initialData.banniere || ''
    selectedFile.value = undefined
  } else {
    // Formulaire vierge
    formData.titre = { francais: '', anglais: '', espagnol: '', italien: '' }
    formData.description = { francais: '', anglais: '', espagnol: '', italien: '' }
    formData.annee = new Date().getFullYear().toString()
    formData.collection = ''
    formData.couleurCadre = 'FFFFFF'
    formData.couleurTexte = '000000'
    formData.categories = []
    formData.banniere = undefined
    couleurCadreText.value = 'FFFFFF'
    couleurTexteText.value = '000000'
    previewUrl.value = ''
    selectedFile.value = undefined
  }
}

// ============= MÉTHODES POUR LES FICHES =============

// Transformer les données de fiche du backend vers le format frontend
const transformFicheToDisplay = (fiche: LookbookFiche): LookbookFicheDisplay => {
  return {
    id: fiche.id_mod_lookbook_fiche,
    image_url: fiche.image_url ? getSellerImageUrl(fiche.image_url) : undefined,
    couverture: fiche.couverture === 1,
    ordre: fiche.ordre,
    date_creation: fiche.date_creation,
    description: {
      francais: fiche.description_fr || '',
      anglais: fiche.description_en || '',
      espagnol: fiche.description_es || '',
      italien: fiche.description_it || '',
    },
  }
}

// Charger les fiches du lookbook
const loadFiches = async () => {
  if (!props.initialData?.id) return
  
  try {
    const fichesData = await getLookbookFiches(props.initialData.id)
    fiches.value = fichesData.map(transformFicheToDisplay)
  } catch (error) {
    show('error', 'Erreur lors du chargement des fiches')
  }
}

// Ajouter une nouvelle fiche
const handleAddFiche = async (file: File | undefined) => {
  if (!file || !props.initialData?.id) return
  
  isUploadingFiche.value = true
  
  try {
    // Créer la fiche
    const newFiche = await createLookbookFiche(
      props.initialData.id, 
      fiches.value.length === 0 // Première fiche = couverture
    )
    
    // Upload de l'image
    const uploadResult = await uploadLookbookFiche(
      props.initialData.id,
      newFiche.id_mod_lookbook_fiche,
      file
    )
    
    if (uploadResult.success) {
      // Recharger les fiches
      await loadFiches()
      show('success', 'Fiche ajoutée avec succès')
    } else {
      show('error', uploadResult.message)
    }
  } catch (error) {
    show('error', 'Erreur lors de l\'ajout de la fiche')
  } finally {
    isUploadingFiche.value = false
  }
}

// Supprimer une fiche
const handleDeleteFiche = async (fiche: LookbookFicheDisplay) => {
  openDeleteModal(fiche)
}

// Définir une fiche comme couverture
const handleSetCouverture = async (fiche: LookbookFicheDisplay) => {
  try {
    await setFicheCouverture(fiche.id)
    await loadFiches()
    show('success', 'Couverture mise à jour')
  } catch (error) {
    show('error', 'Erreur lors de la mise à jour de la couverture')
  }
}

// Changer l'ordre d'une fiche
const handleChangeOrdre = async (fiche: LookbookFicheDisplay, direction: 'prev' | 'next') => {
  try {
    await changeFicheOrdre(fiche.id, direction)
    await loadFiches()
  } catch (error) {
    show('error', 'Erreur lors du changement d\'ordre')
  }
}

// Charger les fiches quand on passe en mode édition
watch(() => props.initialData?.id, (newId) => {
  if (newId) {
    loadFiches()
  } else {
    fiches.value = []
  }
}, { immediate: true })
</script> 
